import React from 'react';
import PlaidLink from '../Plaid/PlaidLink';
import { getPlaidStatus } from '../../services/plaidService';
import { activationService } from '../../services/activationService';
import { useAuth } from '../../state/AuthContext';
import { useConversation } from '../../state/ConversationContext';
import ActivationPanel from './ActivationPanel';
import { getUserFacingError } from '../../lib/userFacingErrors';
import {
  getActivationStep,
  shouldShowActivation,
} from './activationState';

export default function ActivationExperience() {
  const { refreshBilling } = useAuth();
  const { currentThreadId, messages, sendText, threads } = useConversation();
  const [plaidStatus, setPlaidStatus] = React.useState(null);
  const [loadingStatus, setLoadingStatus] = React.useState(true);
  const [plaidLoading, setPlaidLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [plaidSkippedThisSession, setPlaidSkippedThisSession] = React.useState(false);
  const selectedAccountTypeRef = React.useRef(null);

  const loadPlaidStatus = React.useCallback(async () => {
    try {
      setLoadingStatus(true);
      const status = await getPlaidStatus();
      setPlaidStatus(status);
      setError(null);
    } catch (err) {
      setError(getUserFacingError(err, 'activation'));
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  React.useEffect(() => {
    loadPlaidStatus();
  }, [loadPlaidStatus]);

  const hasPlaidConnection = Boolean(plaidStatus?.has_connections);
  const hasSentMessage = messages.length > 0 || threads.length > 0;
  const step = getActivationStep({
    hasProfile: true,
    hasPlaidConnection,
    hasSentMessage,
    plaidSkippedThisSession,
  });

  const eventBase = {
    source: 'activation',
    step,
    has_plaid_connection: hasPlaidConnection,
    has_existing_threads: threads.length > 0,
  };

  React.useEffect(() => {
    if (currentThreadId || loadingStatus || !shouldShowActivation({ step })) return;

    if (step === 'connect') {
      activationService.trackEvent('activation_connect_viewed', eventBase);
    } else if (step === 'prompts') {
      activationService.trackEvent('activation_prompt_viewed', eventBase);
    }
    // Only track when the visible step changes for this mounted flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentThreadId, loadingStatus, step]);

  if (currentThreadId || loadingStatus || !shouldShowActivation({ step })) return null;

  const handlePlaidSuccess = async () => {
    activationService.trackEvent('activation_plaid_connect_succeeded', {
      ...eventBase,
      step: 'connect',
      account_type: selectedAccountTypeRef.current,
    });
    await loadPlaidStatus();
    await refreshBilling?.();
  };

  const handlePlaidExit = (err) => {
    if (err) setError(getUserFacingError(err, 'plaid-link'));
    activationService.trackEvent('activation_plaid_connect_exited', {
      ...eventBase,
      step: 'connect',
      account_type: selectedAccountTypeRef.current,
      reason: err ? 'error' : 'exit',
    });
  };

  const handleSkipPlaid = () => {
    activationService.trackEvent('activation_plaid_skipped', eventBase);
    setPlaidSkippedThisSession(true);
    setError(null);
  };

  const handlePromptSelected = async (prompt) => {
    try {
      setError(null);
      activationService.trackEvent('activation_prompt_clicked', {
        ...eventBase,
        prompt_id: prompt.id,
      });
      await sendText(prompt.text);
      activationService.trackEvent('activation_first_message_sent', {
        ...eventBase,
        prompt_id: prompt.id,
      });
    } catch (err) {
      setError(getUserFacingError(err, 'activation'));
    }
  };

  return (
    <ActivationPanel
      step={step}
      hasPlaidConnection={hasPlaidConnection}
      isConnecting={plaidLoading}
      error={error}
      connectControl={
        <PlaidLink
          buttonLabel="Connect account"
          expandedOptions
          onAccountTypeSelected={(accountType) => {
            selectedAccountTypeRef.current = accountType;
            activationService.trackEvent('activation_plaid_connect_clicked', {
              ...eventBase,
              step: 'connect',
              account_type: accountType,
            });
          }}
          onSuccess={handlePlaidSuccess}
          onExit={handlePlaidExit}
          onLoadingStateChange={setPlaidLoading}
          onUpgradeRequired={(message) => setError({
            title: 'Account limit reached',
            message,
            severity: 'warning',
          })}
        />
      }
      onSkipPlaid={handleSkipPlaid}
      onPromptSelected={handlePromptSelected}
    />
  );
}
